import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ListQueryService } from 'pbc.angular';
import { ISelection } from 'pbc.types';

import { IOffeneGutachtenQueryFilter, IOffeneGutachtenQueryShapes, IOffeneGutachtenQuerySorter, IOffeneGutachtenRequest, IOffeneGutachtenResponse } from 'fa-kt.types';

@Injectable({
  providedIn: 'root',
})
export class OffeneGutachtenService extends ListQueryService<IOffeneGutachtenRequest, IOffeneGutachtenResponse, IOffeneGutachtenQueryShapes, IOffeneGutachtenQueryFilter, IOffeneGutachtenQuerySorter> {
  static instance: OffeneGutachtenService;
  contentKey: keyof IOffeneGutachtenResponse = 'offeneGutachten';
  route = 'besichtigungen/offene-gutachten';
  override dependencies = [
    'besichtigungen/written-besichtigungs-status',
    'besichtigungen/removed-besichtigungs-status',
    'gutachten/written-bewertungs-anlass',
    'gutachten/removed-bewertungs-anlass',
    'gutachten/written-gutachten-status',
    'gutachten/removed-gutachten-status',
    'kunden/written-kunden-art',
    'kunden/removed-kunden-art',
    'kunden/written-kunden-gruppe',
    'kunden/removed-kunden-gruppe',
    'objekte/written-objekt-art',
    'objekte/removed-objekt-art',
    'objekte/written-objekt-nutzung',
    'objekte/removed-objekt-nutzung',
    'personen/written-standort',
    'personen/removed-standort',
    'projekte/written-projekt-art',
    'projekte/removed-projekt-art',
    'projekte/written-projekt-status',
    'projekte/removed-projekt-status',
    'besichtigungen/besichtigung-entfernt',
    'besichtigungen/besichtigung-geordnet',
    'besichtigungen/besichtigung-gespeichert',
    'besichtigungen/tour-entfernt',
    'besichtigungen/tour-gespeichert',
    'besichtigungen/tour-gestartet',
    'einstellungen/kalkulation-hochgeladen',
    'gutachten/gutachten-entfernt',
    'gutachten/gutachten-gespeichert',
    'projekte/projekt-archiviert',
    'projekte/projekt-entfernt',
    'projekte/projekt-gespeichert',
    'projekte/projekt-kopiert',
    'projekte/projekt-reaktiviert',
    'kunden/abteilung-entfernt',
    'kunden/abteilung-gespeichert',
    'kunden/fond-entfernt',
    'kunden/fond-gespeichert',
    'kunden/kunde-entfernt',
    'kunden/kunde-gespeichert',
    'kunden/kunden-finanzen-gespeichert',
    'objekte/objekt-entfernt',
    'objekte/objekt-gespeichert',
  ];
  override replacements: { [key: string]: string }[] = [
    {
      'gutachten.bewertungsAnlass': 'projekt.bewertungsAnlass',
    },
  ];

  constructor() {
    super();
    if (!OffeneGutachtenService.instance) OffeneGutachtenService.instance = this;
    return OffeneGutachtenService.instance;
  }

  getProjektAbteilung(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['projekt.abteilung']?.find((v) => v.value === value);
  }

  get projektAbteilung(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['projekt.abteilung'] as ISelection[]) : [])));
  }

  getObjektBereiche(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['objekt.bereiche']?.find((v) => v.value === value);
  }

  get objektBereiche(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['objekt.bereiche'] as ISelection[]) : [])));
  }

  getBesichtigungBesichtigungsStatus(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['besichtigung.besichtigungsStatus']?.find((v) => v.value === value);
  }

  get besichtigungBesichtigungsStatus(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['besichtigung.besichtigungsStatus'] as ISelection[]) : [])));
  }

  getGutachtenBewertungsAnlass(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['gutachten.bewertungsAnlass']?.find((v) => v.value === value);
  }

  get gutachtenBewertungsAnlass(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['gutachten.bewertungsAnlass'] as ISelection[]) : [])));
  }

  getProjektFond(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['projekt.fond']?.find((v) => v.value === value);
  }

  get projektFond(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['projekt.fond'] as ISelection[]) : [])));
  }

  getGutachtenBewertungsStatus(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['gutachten.bewertungsStatus']?.find((v) => v.value === value);
  }

  get gutachtenBewertungsStatus(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['gutachten.bewertungsStatus'] as ISelection[]) : [])));
  }

  getProjektKunde(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['projekt.kunde']?.find((v) => v.value === value);
  }

  get projektKunde(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['projekt.kunde'] as ISelection[]) : [])));
  }

  getProjektBank(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['projekt.bank']?.find((v) => v.value === value);
  }

  get projektBank(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['projekt.bank'] as ISelection[]) : [])));
  }

  getKundeKundenArt(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['kunde.kundenArt']?.find((v) => v.value === value);
  }

  get kundeKundenArt(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['kunde.kundenArt'] as ISelection[]) : [])));
  }

  getKundeKundenGruppe(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['kunde.kundenGruppe']?.find((v) => v.value === value);
  }

  get kundeKundenGruppe(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['kunde.kundenGruppe'] as ISelection[]) : [])));
  }

  getProjektBesichtigungen(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['projekt.besichtigungen']?.find((v) => v.value === value);
  }

  get projektBesichtigungen(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['projekt.besichtigungen'] as ISelection[]) : [])));
  }

  getProjektGutachten(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['projekt.gutachten']?.find((v) => v.value === value);
  }

  get projektGutachten(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['projekt.gutachten'] as ISelection[]) : [])));
  }

  getProjektPruefung(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['projekt.pruefung']?.find((v) => v.value === value);
  }

  get projektPruefung(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['projekt.pruefung'] as ISelection[]) : [])));
  }

  getBesichtigungMitarbeiter(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['besichtigung.mitarbeiter']?.find((v) => v.value === value);
  }

  get besichtigungMitarbeiter(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['besichtigung.mitarbeiter'] as ISelection[]) : [])));
  }

  getGutachtenObjekt(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['gutachten.objekt']?.find((v) => v.value === value);
  }

  get gutachtenObjekt(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['gutachten.objekt'] as ISelection[]) : [])));
  }

  getGutachtenObjektArt(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['gutachten.objektArt']?.find((v) => v.value === value);
  }

  get gutachtenObjektArt(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['gutachten.objektArt'] as ISelection[]) : [])));
  }

  getGutachtenObjektNutzung(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['gutachten.objektNutzung']?.find((v) => v.value === value);
  }

  get gutachtenObjektNutzung(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['gutachten.objektNutzung'] as ISelection[]) : [])));
  }

  getProjektProjektArt(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['projekt.projektArt']?.find((v) => v.value === value);
  }

  get projektProjektArt(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['projekt.projektArt'] as ISelection[]) : [])));
  }

  getProjektProjektStatus(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['projekt.projektStatus']?.find((v) => v.value === value);
  }

  get projektProjektStatus(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['projekt.projektStatus'] as ISelection[]) : [])));
  }

  getProjektStandort(value: string): ISelection | undefined {
    return this.shapes$.getValue()?.['projekt.standort']?.find((v) => v.value === value);
  }

  get projektStandort(): Observable<ISelection[]> {
    return this.shapes$.pipe(map((shapes) => (shapes ? (shapes['projekt.standort'] as ISelection[]) : [])));
  }
}
